// Create a new file called CustomDatePicker.tsx
import React, { useState } from 'react';
import { Box, Button, GlobalStyles, Typography, InputAdornment } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useTheme from '@mui/material/styles/useTheme';
import CalendarIcon from '../icons/Calendar.svg';
import { OutlinedInputProps } from '@mui/material/OutlinedInput';

interface CustomDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  disableFuture?: boolean;
  viewType?: 'day' | 'week' | 'month';
  onChangeViewType?: (view: 'day' | 'week' | 'month') => void;
}

const globalStyles = (
  <GlobalStyles
    styles={{
      '.MuiPickersLayout-root': {
        padding: '0 !important',
        margin: '0 !important',
        display: 'block !important', // Ensure no flex/grid is applied
      },
      '.MuiPickersCalendarHeader-label': {
        color: 'blue !important', // Change the month color here
      },
      '.MuiPickersArrowSwitcher-button': {
        color: 'blue !important', // Change the arrow icon color here
      },
    }}
  />
);

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  disableFuture,
  viewType = 'day',
  onChangeViewType,
}) => {
  const theme = useTheme();
  const [view, setView] = useState<'day' | 'week' | 'month'>(viewType);
  const [pickerOpen, setPickerOpen] = useState<boolean>(false);

  const handleDateChange = (newDate: Date | null) => {
    onChange(newDate);
    // You could implement different behavior based on selected view
  };

  const handleViewChange = (newView: 'day' | 'week' | 'month') => {
    setView(newView);
    onChangeViewType?.(newView);
    // Here you would implement the specific view functionality
    // For example, if "Week" is selected, you might want to select the entire week
  };

  // Custom render for the DatePicker toolbar
  const CustomToolbar = ({ ...props }) => {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Select
        </Typography>

        {/* View selection tabs */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: theme.palette.grey[100],
            borderRadius: '50px',

            mb: 3,
          }}
        >
          <Button
            variant={view === 'day' ? 'contained' : 'text'}
            color="primary"
            onClick={() => handleViewChange('day')}
            sx={{
              flex: 1,
              borderRadius: '24px',
              boxShadow: view === 'day' ? 1 : 0,
            }}
          >
            Day
          </Button>
          <Button
            variant={view === 'week' ? 'contained' : 'text'}
            color="primary"
            onClick={() => handleViewChange('week')}
            sx={{
              flex: 1,
              borderRadius: '24px',
              boxShadow: view === 'week' ? 1 : 0,
            }}
          >
            Week
          </Button>
          <Button
            variant={view === 'month' ? 'contained' : 'text'}
            color="primary"
            onClick={() => handleViewChange('month')}
            sx={{
              flex: 1,
              borderRadius: '24px',
              boxShadow: view === 'month' ? 1 : 0,
            }}
          >
            Month
          </Button>
        </Box>

        <Box sx={{ borderTop: `2px dotted ${theme.palette.divider}` }}></Box>

        {/* Default toolbar is rendered here */}
        <Box {...props} />
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {globalStyles}
      <DatePicker
        open={pickerOpen}
        onOpen={() => setPickerOpen(true)}
        onClose={() => setPickerOpen(false)}
        value={value}
        onChange={handleDateChange}
        disableFuture={disableFuture}
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          textField: {
            size: 'small',
            inputProps: {
              readOnly: true, // disables manual typing
            },
            InputProps: {
              endAdornment: (
                <InputAdornment
                  position="end"
                  onClick={() => setPickerOpen(true)}
                  sx={{
                    cursor: 'pointer',
                    ml: 1.5, // Add margin to create space between text and icon
                  }}
                >
                  <img src={CalendarIcon} alt="calendar" />
                </InputAdornment>
              ),
              sx: {
                color: theme.palette.primary.main,
                fontWeight: theme.typography.h1.fontWeight,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '50px',
                },
                width: '175px', // Set specific width for the input
                '& .MuiOutlinedInput-input': {
                  padding: '8px 14px', // Adjust padding to fine-tune spacing
                },
              },
            } as Partial<OutlinedInputProps>,
          },
          toolbar: {
            toolbarFormat: 'MMMM yyyy',
            toolbarPlaceholder: 'Select',
          },
        }}
        format="dd MMM yyyy"
      />
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
